import { useCallback, useEffect, useState } from "react";
import { Plus, BookOpen, Image as ImageIcon, Network, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errorText } from "@/lib/api";
import { TermForm } from "@/components/TermForm";
import { TeacherTermRow } from "@/components/TeacherTermRow";
import { BulkGenerate } from "@/components/BulkGenerate";
import { MapEditor } from "@/components/MapEditor";
import { ClassesPanel } from "@/components/ClassesPanel";
import { StudySheetsPanel } from "@/components/StudySheetsPanel";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const Stat = ({ icon: Icon, label, value, color, testId }) => (
  <div className="ink-box-sm flex items-center gap-3 rounded-2xl bg-white p-4" data-testid={testId}>
    <span className={`flex h-10 w-10 items-center justify-center rounded-xl border-2 border-[var(--ink)] ${color}`}><Icon className="h-5 w-5" /></span>
    <div>
      <p className="font-display text-2xl font-bold leading-none">{value}</p>
      <p className="text-xs font-bold uppercase tracking-wide text-neutral-600">{label}</p>
    </div>
  </div>
);

export default function TeacherDashboard() {
  const [terms, setTerms] = useState([]);
  const [stats, setStats] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [editing, setEditing] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [filter, setFilter] = useState("");
  const [mapTerm, setMapTerm] = useState(null);

  const load = useCallback(async () => {
    const [t, s] = await Promise.all([api.get("/terms"), api.get("/teacher/stats")]);
    setTerms(t.data);
    setStats(s.data);
  }, []);

  useEffect(() => { load(); api.get("/subjects").then((r) => setSubjects(r.data)); }, [load]);

  const pending = stats?.pending > 0;
  useEffect(() => {
    if (!pending) return;
    const iv = setInterval(load, 5000);
    return () => clearInterval(iv);
  }, [pending, load]);

  const generate = async (term, kind) => {
    try {
      await api.post(`/terms/${term.id}/${kind}`);
      toast.success(kind === "comic" ? "Generazione fumetto avviata (circa 30 secondi)" : "Generazione mappa avviata (pochi secondi)");
      load();
    } catch (e) {
      toast.error(errorText(e));
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/terms/${toDelete.id}`);
      toast.success("Termine eliminato");
      load();
    } catch (e) {
      toast.error(errorText(e));
    }
    setToDelete(null);
  };

  const shown = terms.filter((t) => !filter || t.word.toLowerCase().includes(filter.toLowerCase()));

  return (
    <main className="mx-auto max-w-6xl px-5 pb-24 pt-10 sm:px-8" data-testid="teacher-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-extrabold uppercase tracking-widest text-neutral-600">Area docente</p>
          <h1 className="font-display text-4xl font-bold sm:text-5xl">Il tuo glossario</h1>
        </div>
        <button onClick={() => { setEditing(null); setFormOpen(true); }} data-testid="add-term-button" className="ink-box ink-press flex items-center gap-2 rounded-full bg-[var(--sun)] px-5 py-3 font-extrabold">
          <Plus className="h-5 w-5" /> Nuovo termine
        </button>
      </div>

      <div className="mt-8 grid grid-cols-2 gap-4 md:grid-cols-4">
        <Stat icon={BookOpen} label="Termini" value={stats?.terms ?? "–"} color="bg-[var(--sun)]" testId="stat-terms" />
        <Stat icon={ImageIcon} label="Fumetti" value={stats?.comics ?? "–"} color="bg-[var(--coral)]" testId="stat-comics" />
        <Stat icon={Network} label="Mappe" value={stats?.maps ?? "–"} color="bg-[var(--sky)] text-white" testId="stat-maps" />
        <Stat icon={Loader2} label="In corso" value={stats?.pending ?? "–"} color="bg-[var(--mint)]" testId="stat-pending" />
      </div>

      <Tabs defaultValue="terms" className="mt-10">
        <TabsList className="h-auto flex-wrap gap-2 bg-transparent p-0">
          <TabsTrigger value="terms" data-testid="tab-terms" className="rounded-full border-2 border-[var(--ink)] px-4 py-2 font-extrabold data-[state=active]:bg-[var(--ink)] data-[state=active]:text-white">Termini</TabsTrigger>
          <TabsTrigger value="classes" data-testid="tab-classes" className="rounded-full border-2 border-[var(--ink)] px-4 py-2 font-extrabold data-[state=active]:bg-[var(--ink)] data-[state=active]:text-white">Classi e risultati</TabsTrigger>
          <TabsTrigger value="sheets" data-testid="tab-sheets" className="rounded-full border-2 border-[var(--ink)] px-4 py-2 font-extrabold data-[state=active]:bg-[var(--ink)] data-[state=active]:text-white">Fogli di studio</TabsTrigger>
        </TabsList>
        <TabsContent value="terms">
      <BulkGenerate terms={terms} onStarted={load} />

      <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filtra termini…" data-testid="dashboard-filter-input" className="mt-10 w-full rounded-xl border-2 border-[var(--ink)] bg-white px-4 py-2.5 font-semibold outline-none sm:max-w-xs" />

      <div className="mt-5 space-y-3" data-testid="teacher-terms-list">
        {shown.map((t) => (
          <TeacherTermRow key={t.id} term={t} onEdit={() => { setEditing(t); setFormOpen(true); }} onDelete={() => setToDelete(t)} onComic={() => generate(t, "comic")} onMap={() => generate(t, "map")} onEditMap={() => setMapTerm(t)} />
        ))}
      </div>
        </TabsContent>
        <TabsContent value="classes" className="mt-6"><ClassesPanel /></TabsContent>
        <TabsContent value="sheets" className="mt-6"><StudySheetsPanel terms={terms} /></TabsContent>
      </Tabs>

      <TermForm open={formOpen} onClose={() => setFormOpen(false)} term={editing} subjects={subjects} onSaved={load} />
      <MapEditor term={mapTerm} onClose={() => setMapTerm(null)} onSaved={load} />

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="rounded-3xl border-[2.5px] border-[var(--ink)]" data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare "{toDelete?.word}"?</AlertDialogTitle>
            <AlertDialogDescription>Il termine sparirà dal glossario degli alunni.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-button">Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="delete-confirm-button" className="bg-[var(--coral)]">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </main>
  );
}
